import React, { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs'; // dayjs für TimePicker importieren
import customParseFormat from 'dayjs/plugin/customParseFormat'; // Plugin für HH:mm Format
import ImageUpload from './ImageUpload'; // Importiere die korrekte Upload-Komponente
// import './RegistrationForm.css'; // Entferne den CSS Import
import {
    Form, Input, Checkbox, Row, Col, Typography, Divider, TimePicker, Select, Button, Space,
    AutoComplete
} from 'antd';
import { MailOutlined, LockOutlined, UserOutlined, ShopOutlined, PhoneOutlined, HomeOutlined, GlobalOutlined, InfoCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';

dayjs.extend(customParseFormat); // Plugin aktivieren

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Konstante für Tage, um Wiederholungen zu vermeiden
const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const dayLabels = {
    monday: 'Montag',
    tuesday: 'Dienstag',
    wednesday: 'Mittwoch',
    thursday: 'Donnerstag',
    friday: 'Freitag',
    saturday: 'Samstag',
    sunday: 'Sonntag',
};

// Debounce function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

const OptimizedRegistrationForm = ({ initialData, onChange, fieldErrors = {} }) => {
    const [form] = Form.useForm(); // Antd Form Hook
    // Behalte formData State bei, um Änderungen an den Parent weiterzugeben,
    // aber Antd Form verwaltet den Input-Zustand selbst.
    const [formData, setFormData] = useState(initialData || {});
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const searchTimeoutRef = useRef(null); // Ref for debounce timeout

    // Helper function to set TimePicker values in the form
    // Moved definition here to be available in useEffect
    const setTimePickerValue = useCallback((day, field, timeString) => {
        const fieldName = `openingHours_${day}_${field}`;
        const value = timeString ? dayjs(timeString, 'HH:mm') : null;
        form.setFieldsValue({ [fieldName]: value });
    }, [form]); // Dependency is only form

    // Effect to synchronize Antd Form with initialData and set initial TimePicker values
    useEffect(() => {
        const dataToSet = initialData || {};
        form.setFieldsValue(dataToSet); // Set basic fields
        setFormData(dataToSet); // Set local state

        // Explicitly set TimePicker values based on initialData
        daysOfWeek.forEach(day => {
            const openTime = dataToSet.openingHours?.[day]?.open || '';
            const closeTime = dataToSet.openingHours?.[day]?.close || '';
            setTimePickerValue(day, 'open', openTime);
            setTimePickerValue(day, 'close', closeTime);
        });
    }, [initialData, form, setTimePickerValue]); // Include setTimePickerValue

    // Fetch address suggestions from Nominatim API
    const fetchAddressSuggestions = async (query) => {
        if (!query || query.length < 3) {
            setAddressSuggestions([]);
            return;
        }
        setLoadingSuggestions(true);
        try {
            // Using Nominatim API (ensure compliance with usage policy)
            // Limit search to Germany (countrycodes=de) and include address details
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&countrycodes=de&limit=5`
            );
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            const options = data.map(item => ({
                value: item.display_name, // Full display name for the dropdown
                label: item.display_name,
                // Store structured address details for autofill
                addressDetails: item.address 
            }));
            setAddressSuggestions(options);
        } catch (error) {
            console.error("Error fetching address suggestions:", error);
            setAddressSuggestions([]); // Clear suggestions on error
        } finally {
            setLoadingSuggestions(false);
        }
    };

    // Debounced version of the fetch function
    const debouncedFetchSuggestions = useCallback(debounce(fetchAddressSuggestions, 500), []);

    // Handler for AutoComplete search input change
    const handleAddressSearch = (value) => {
        debouncedFetchSuggestions(value);
    };

    // Handler for selecting an address suggestion
    const handleAddressSelect = (value, option) => {
        const details = option.addressDetails;
        if (!details) return;

        // Extract address components (adjust keys based on Nominatim response)
        const street = details.road || '';
        const houseNumber = details.house_number || '';
        const postcode = details.postcode || '';
        const city = details.city || details.town || details.village || '';

        const fullStreetAddress = `${street} ${houseNumber}`.trim();

        // Update Ant Design form fields
        form.setFieldsValue({
            address: fullStreetAddress,
            postalCode: postcode,
            city: city,
        });

        // Manually trigger onChange for the parent component with updated fields
        handleFormValuesChange(
            { address: fullStreetAddress, postalCode: postcode, city: city }, 
            { ...formData, address: fullStreetAddress, postalCode: postcode, city: city }
        );
    };

    // Callback for any Antd Form field change
    const handleFormValuesChange = useCallback((changedValues, allValues) => {
        // Create a mutable copy to work with
        let currentFormData = { ...formData }; 
        let updatedAllValues = { ...allValues }; // Copy allValues as well

        // Special handling for TimePicker values (they might be dayjs objects in allValues)
        let openingHoursChanged = false;
        daysOfWeek.forEach(day => {
            const openFieldName = `openingHours_${day}_open`;
            const closeFieldName = `openingHours_${day}_close`;

            // Check if these fields exist in allValues (meaning they were likely just changed via TimePicker)
            if (updatedAllValues.hasOwnProperty(openFieldName) || updatedAllValues.hasOwnProperty(closeFieldName)) {
                openingHoursChanged = true;
                const openTimeObj = updatedAllValues[openFieldName];
                const closeTimeObj = updatedAllValues[closeFieldName];
                
                const openTimeString = openTimeObj ? dayjs(openTimeObj).format('HH:mm') : '';
                const closeTimeString = closeTimeObj ? dayjs(closeTimeObj).format('HH:mm') : '';

                // Update the working copy of formData
                if (!currentFormData.openingHours) currentFormData.openingHours = {};
                if (!currentFormData.openingHours[day]) currentFormData.openingHours[day] = {};
                currentFormData.openingHours[day].open = openTimeString;
                currentFormData.openingHours[day].close = closeTimeString;

                // Remove the temporary dayjs fields from updatedAllValues before merging
                delete updatedAllValues[openFieldName];
                delete updatedAllValues[closeFieldName];
            }
        });
        
        // Merge other changed values into the working copy
        currentFormData = { ...currentFormData, ...updatedAllValues };

        // Handle address suggestion clearing (if manual change)
        if (changedValues.hasOwnProperty('address') || changedValues.hasOwnProperty('postalCode') || changedValues.hasOwnProperty('city')) {
            setAddressSuggestions([]);
        }

        setFormData(currentFormData); // Update state
        onChange(currentFormData); // Notify parent
    }, [onChange, formData]);

    // --- Opening Hours Handlers --- //

    // Direct change from TimePicker (already handled by onValuesChange, but keep specific logic if needed)
    const handleOpeningHoursChangeDirect = useCallback((day, field, time) => {
        // This might be redundant if onValuesChange handles it correctly.
        // However, it can be useful for immediate state updates or complex logic.
        const timeString = time ? time.format('HH:mm') : '';
        const newOpeningHours = {
            ...formData.openingHours,
            [day]: {
                ...formData.openingHours?.[day],
                [field]: timeString
            }
        };
        const updatedFormData = { ...formData, openingHours: newOpeningHours };
        setFormData(updatedFormData);
        onChange(updatedFormData);
    }, [formData, onChange]);

    const handleCheckboxChange = useCallback((day, type, checked) => {
        let openTime = formData.openingHours?.[day]?.open || '';
        let closeTime = formData.openingHours?.[day]?.close || '';

        if (type === 'closed') {
            openTime = checked ? '' : '09:00';
            closeTime = checked ? '' : '18:00';
        } else if (type === '24h') {
            openTime = checked ? '00:00' : '09:00';
            closeTime = checked ? '23:59' : '18:00';
        }

        // Prepare updated opening hours for this day
        const dayUpdate = { open: openTime, close: closeTime };

        // Update local state and notify parent
        const newOpeningHours = { ...formData.openingHours, [day]: dayUpdate };
        const updatedFormData = { ...formData, openingHours: newOpeningHours };
        setFormData(updatedFormData);
        onChange(updatedFormData);

        // Update the visual TimePicker fields *after* state update
        setTimePickerValue(day, 'open', openTime);
        setTimePickerValue(day, 'close', closeTime);

    }, [formData, onChange, setTimePickerValue]);

    const handleAllDays24Hours = useCallback((is24Hours) => {
        const newOpeningHours = { ...formData.openingHours }; // Start with current hours
        const fieldsToUpdate = {}; // Track updates for setFieldsValue

        daysOfWeek.forEach(day => {
            const openTime = is24Hours ? '00:00' : (day === 'sunday' ? '' : (day === 'saturday' ? '10:00' : '09:00'));
            const closeTime = is24Hours ? '23:59' : (day === 'sunday' ? '' : (day === 'saturday' ? '16:00' : '18:00'));
            newOpeningHours[day] = { open: openTime, close: closeTime };
            
            // Prepare updates for Antd Form TimePickers (use field names from form)
            fieldsToUpdate[`openingHours_${day}_open`] = openTime ? dayjs(openTime, 'HH:mm') : null;
            fieldsToUpdate[`openingHours_${day}_close`] = closeTime ? dayjs(closeTime, 'HH:mm') : null;
        });

        // Update local state first
        const updatedFormData = { ...formData, openingHours: newOpeningHours };
        setFormData(updatedFormData);
        onChange(updatedFormData);

        // Update the Antd Form fields visually *after* state update
        form.setFieldsValue(fieldsToUpdate);

    }, [formData, onChange, form, setTimePickerValue]);

    // Handler für Marken wird durch Antd Form Item gehandhabt
    // const handleBrandChange = useCallback((selectedBrands) => { ... });

    const handleImagesChange = useCallback((newImages) => {
        // Stellt sicher, dass newImages immer ein Array ist (vom Uploader)
        const imageArray = Array.isArray(newImages) ? newImages : [];
        handleFormValuesChange({ images: imageArray }, { ...formData, images: imageArray });
    }, [formData, handleFormValuesChange]);

    // Handler für AGB wird durch Antd Form Item gehandhabt
    // const handleTermsChange = useCallback((e) => { ... });

    // Erweiterte Markenliste
    const availableBrands = [
        // Klassiker & Bekannte
        'General', 'Göteborgs Rapé', 'Ettan', 'Odens', 'Siberia',
        'Skruf', 'LD', 'Kaliber', 'Knox', 'Granit',
        // Nicotine Pouches (oft als Snus bezeichnet)
        'Lyft', 'Velo', 'Zyn', 'White Fox', 'Shiro',
        'Skruf Super White', 'Nordic Spirit', 'On!', 'Pablo', 'Killa',
        // Weitere
        'Thunder', 'XR', 'Lundgrens', 'LOOP', 'Volt',
        'Swave', 'Ace', 'Kapten', 'Jakobsson\'s', 'Nick & Johnny',
        'Rite', 'Dope', 'Snatch', 'Grant', 'HIT'
        // Liste kann bei Bedarf weiter ergänzt werden
    ].sort(); // Alphabetisch sortieren

    return (
        // Das Haupt-Formular-Element von Ant Design
        <Form
            form={form} // Verbindet den Form Hook
            layout="vertical"
            onValuesChange={handleFormValuesChange} // Ruft Callback bei jeder Änderung auf
            initialValues={initialData} // Use initialData to set initial form state
        >
            <Title level={3}>Basisinformationen</Title>

            {/* Shop-Details Sektion */}
            <Divider>Shop-Informationen</Divider>

            <Form.Item
                name="name"
                label="Shop-Name"
                rules={[{ required: true, message: 'Bitte geben Sie den Namen Ihres Shops ein.' }]}
                validateStatus={fieldErrors?.name ? 'error' : undefined}
                help={fieldErrors?.name}
            >
                <Input prefix={<ShopOutlined />} placeholder="Name des Kiosks, Tankstelle, etc." />
            </Form.Item>

             <Row gutter={16}>
                 <Col xs={24} sm={16}>
                    <Form.Item
                        name="address"
                        label="Straße & Hausnummer"
                        rules={[{ required: true, message: 'Bitte geben Sie die Adresse ein.' }]}
                        validateStatus={fieldErrors?.address ? 'error' : undefined}
                        help={fieldErrors?.address}
                    >
                        <AutoComplete
                            options={addressSuggestions}
                            onSelect={handleAddressSelect}
                            onSearch={handleAddressSearch}
                            placeholder="Musterstraße 123 eingeben..."
                            notFoundContent={loadingSuggestions ? 'Suche...' : null}
                            style={{ width: '100%' }}
                        >
                            <Input prefix={<HomeOutlined />} />
                         </AutoComplete>
                     </Form.Item>
                 </Col>
                 <Col xs={12} sm={8}>
                    <Form.Item
                        name="postalCode"
                        label="PLZ"
                        rules={[
                            { required: true, message: 'Bitte PLZ eingeben.' },
                            { pattern: /^\d{5}$/, message: 'Bitte eine 5-stellige PLZ eingeben.' }
                         ]}
                        validateStatus={fieldErrors?.postalCode ? 'error' : undefined}
                        help={fieldErrors?.postalCode}
                    >
                        <Input placeholder="12345" />
                     </Form.Item>
                 </Col>
             </Row>

             <Row gutter={16}>
                <Col xs={12} sm={12}>
                    <Form.Item
                        name="city"
                        label="Stadt"
                        rules={[{ required: true, message: 'Bitte geben Sie die Stadt ein.' }]}
                        validateStatus={fieldErrors?.city ? 'error' : undefined}
                        help={fieldErrors?.city}
                     >
                        <Input placeholder="Musterstadt" />
                    </Form.Item>
                </Col>
                <Col xs={12} sm={12}>
                    <Form.Item
                        name="phone"
                        label="Telefonnummer"
                        rules={[{ required: true, message: 'Bitte geben Sie Ihre Telefonnummer ein.' }]}
                        validateStatus={fieldErrors?.phone ? 'error' : undefined}
                        help={fieldErrors?.phone}
                    >
                        <Input prefix={<PhoneOutlined />} placeholder="+49 123 456789" />
                    </Form.Item>
                 </Col>
            </Row>

            <Form.Item
                name="website"
                label="Webseite (optional)"
                rules={[{ type: 'url', warningOnly: true, message: 'Bitte geben Sie eine gültige URL ein (z.B. https://...)' }]}
                validateStatus={fieldErrors?.website ? 'error' : undefined}
                help={fieldErrors?.website}
            >
                 <Input prefix={<GlobalOutlined />} placeholder="https://www.ihrshop.de" />
            </Form.Item>

            {/* NEUES BESCHREIBUNGSFELD */}
            <Form.Item
                label="Shop Beschreibung (optional)"
                name="description"
                validateStatus={fieldErrors?.description ? 'error' : ''}
                help={fieldErrors?.description || 'Eine kurze Beschreibung Ihres Shops, Sortiments oder Besonderheiten (max. 1000 Zeichen).'}
                rules={[{ max: 1000, message: 'Die Beschreibung darf maximal 1000 Zeichen lang sein.' }]}
            >
                <TextArea
                    rows={4}
                    placeholder="z.B. Kleiner Kiosk mit großer Auswahl an Snus und Nikotinbeuteln. Wir führen Marken wie Siberia, Velo und Odens. Direkt am U-Bahnhof gelegen."
                />
            </Form.Item>

            {/* --- NEUE Öffnungszeiten mit Antd --- */}
            <Divider>Öffnungszeiten</Divider>
            <Space style={{ marginBottom: '16px' }}>
                 <Button onClick={() => handleAllDays24Hours(true)}>Alle Tage 24h</Button>
                 <Button onClick={() => handleAllDays24Hours(false)}>Standardzeiten</Button>
             </Space>
             {daysOfWeek.map(day => {
                // Get values directly from the potentially updated formData state
                const currentDayHours = formData.openingHours?.[day] || {};
                const openTimeValue = currentDayHours.open || '';
                const closeTimeValue = currentDayHours.close || '';
                const isClosed = !openTimeValue && !closeTimeValue;
                const is24h = openTimeValue === '00:00' && closeTimeValue === '23:59';

                return (
                    <Row key={day} gutter={[8, 8]} align="middle" style={{ marginBottom: '8px' }}>
                        <Col xs={24} sm={4} md={3} style={{ fontWeight: 500 }}>{dayLabels[day]}</Col>
                        <Col xs={10} sm={7} md={6}>
                            {/* Use name to link with form state, value controlled visually */}
                            <Form.Item name={`openingHours_${day}_open`} noStyle>
                                <TimePicker
                                    format="HH:mm"
                                    minuteStep={15}
                                    hourStep={1}
                                    placeholder="Öffnet um"
                                    // onChange handler remains for direct input if needed, but state drives display
                                    onChange={(time) => handleOpeningHoursChangeDirect(day, 'open', time)}
                                    style={{ width: '100%' }}
                                    disabled={isClosed || is24h}
                                    // Value is critical for display reflecting state
                                    value={openTimeValue ? dayjs(openTimeValue, 'HH:mm') : null}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={4} sm={2} md={1} style={{ textAlign: 'center' }}>bis</Col>
                        <Col xs={10} sm={7} md={6}>
                            <Form.Item name={`openingHours_${day}_close`} noStyle>
                                <TimePicker
                                    format="HH:mm"
                                    minuteStep={15}
                                    hourStep={1}
                                    placeholder="Schließt um"
                                    onChange={(time) => handleOpeningHoursChangeDirect(day, 'close', time)}
                                    style={{ width: '100%' }}
                                    disabled={isClosed || is24h}
                                    value={closeTimeValue ? dayjs(closeTimeValue, 'HH:mm') : null}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={12} sm={4} md={4}>
                            <Checkbox
                                onChange={(e) => handleCheckboxChange(day, '24h', e.target.checked)}
                                checked={is24h}
                                disabled={isClosed}
                            >
                                24h
                            </Checkbox>
                        </Col>
                        <Col xs={12} sm={4} md={4}>
                            <Checkbox
                                onChange={(e) => handleCheckboxChange(day, 'closed', e.target.checked)}
                                checked={isClosed}
                                disabled={is24h}
                            >
                                Geschlossen
                            </Checkbox>
                        </Col>
                    </Row>
                );
            })}

            {/* --- NEUER Marken-Abschnitt mit Antd --- */}
            <Divider>Marken</Divider>
            <Form.Item
                name="brands"
                label="Welche Snus-Marken führen Sie hauptsächlich?"
                // rules={[{ required: true, message: 'Bitte wählen Sie mindestens eine Marke aus.', type: 'array' }]}
            >
                 <Checkbox.Group style={{ width: '100%' }}>
                     <Row gutter={[8, 8]}>
                        {availableBrands.map(brand => (
                            <Col xs={12} sm={8} md={6} key={brand}>
                                <Checkbox value={brand}>{brand}</Checkbox>
                            </Col>
                        ))}
                     </Row>
                </Checkbox.Group>
            </Form.Item>

            {/* --- NEUER Bilder-Upload mit ImageUpload Komponente --- */}
            <Divider>Bilder hochladen</Divider>
            <Form.Item
                 label={`Laden Sie Bilder Ihres Shops hoch (Logo, Außenansicht, Innenansicht). Max. 8 Bilder.`}
                 // Kein `name`, da der Wert über `handleImagesChange` verwaltet wird
                // Optional: Validierung hinzufügen, falls Bilder erforderlich sind
             >
                <ImageUpload
                     // initialImageList muss die Struktur erwarten, die ImageUpload zurückgibt
                     // (wahrscheinlich [{ uid, name, status, url }])
                     // Konvertiere formData.images, falls nötig
                    initialImageList={formData.images?.map((img, index) => ({
                        uid: img.public_id || `-image-${index}`,
                        name: img.filename || `image-${index}.jpg`,
                        status: 'done', // Annahme: Bestehende Bilder sind bereits hochgeladen
                        url: img.url,
                        public_id: img.public_id // Zusätzliche Info speichern
                    })) || []}
                     onChange={handleImagesChange} // Bindet den Callback
                    maxImages={8}
                 />
             </Form.Item>

            {/* --- NEUER Zustimmungs-Abschnitt mit Antd --- */}
            <Divider>Zustimmung</Divider>
            <Form.Item
                name="acceptTerms"
                valuePropName="checked"
                rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('Sie müssen den AGB und Datenschutzbestimmungen zustimmen.')) }]}
                validateStatus={fieldErrors?.acceptTerms ? 'error' : undefined}
                help={fieldErrors?.acceptTerms}
            >
                <Checkbox>
                    Ich habe die <a href="/privacy" target="_blank" rel="noopener noreferrer">Datenschutzbestimmungen</a> und AGB gelesen und akzeptiere sie.
                </Checkbox>
             </Form.Item>

         </Form>
    );
};

export default OptimizedRegistrationForm;